import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { useNavigate } from 'react-router-dom';
import HealthTracking from './HealthTracking';
import DoctorProfileBuilder from './DoctorProfileBuilder';
import VerificationStatusBanner from './VerificationStatusBanner';
import ContactInfoChange from './ContactInfoChange';
import PhotoPicker from './PhotoPicker';
import { CATEGORY_LABEL_KEYS } from './providers/ProviderResultCard';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Profile Picture Manager (with remove/replace) ─── */
const ProfilePictureManager = ({ sessionToken, onUpdate, currentPicture }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [pictures, setPictures] = useState([]);

  const fetchPictures = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/profile/pictures`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPictures(data.pictures || []);
      }
    } catch (e) { console.error(e); }
  }, [sessionToken]);

  useEffect(() => { fetchPictures(); }, [fetchPictures]);

  const handleUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('image_type', 'profile');
    try {
      await axios.post(`${API}/api/profile/upload-picture`, fd, {
        headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'multipart/form-data' }
      });
      await fetchPictures();
      onUpdate();
    } catch (err) {
      alert(t('profile.alerts.uploadFailed') + ': ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const handleRemove = async (picId) => {
    if (!window.confirm('Remove this profile picture?')) return;
    try {
      await fetch(`${API}/api/profile/picture/${picId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      await fetchPictures();
      onUpdate();
    } catch (e) { alert(t('profile.alerts.removePictureFailed')); }
  };

  const profilePic = pictures.find(p => !p.is_default && p.image_type !== 'gallery') || null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.photos.profilePicture')} *</label>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-200 bg-gray-100 flex items-center justify-center flex-shrink-0">
          {(profilePic || currentPicture) ? (
            <img
              src={profilePic ? (profilePic.is_default ? profilePic.file_url : `${API}${profilePic.file_url}${profilePic.approval_status === 'approved' ? '' : `?auth=${sessionToken}`}`) : currentPicture}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <PhotoPicker
            onConfirm={handleUpload}
            uploading={uploading}
            testId="profile-picture-picker"
            triggerClassName="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            {uploading ? (
              <><span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> {t('profile.photos.uploading')}</>
            ) : (
              <>{profilePic ? t('profile.photos.replacePicture') : t('profile.photos.uploadPicture')}</>
            )}
          </PhotoPicker>
          {profilePic && !profilePic.is_default && (
            <button
              onClick={() => handleRemove(profilePic.id)}
              className="inline-flex items-center px-4 py-2 bg-red-50 text-red-600 text-sm rounded-lg hover:bg-red-100 border border-red-200 transition-colors"
              data-testid="remove-profile-picture-btn"
            >
              {t('profile.photos.removePicture')}
            </button>
          )}
          {profilePic && profilePic.approval_status === 'pending' && (
            <span
              data-testid="profile-picture-pending-badge"
              className="inline-flex w-fit items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-700"
            >
              {t('profile.photos.pendingApproval')}
            </span>
          )}
          {profilePic && profilePic.approval_status === 'rejected' && (
            <span
              data-testid="profile-picture-rejected-badge"
              className="inline-flex w-fit items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-bold text-rose-700"
            >
              {t('profile.photos.rejected')} — {profilePic.rejection_reason || t('profile.photos.rejectedFallback')}
            </span>
          )}
          <p className="text-xs text-gray-400">{t('profile.photos.pictureHint')}</p>
        </div>
      </div>
    </div>
  );
};

/* ─── Facility Images Manager ─── */
const FacilityImagesManager = ({ sessionToken, onUpdate }) => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const maxImages = 6;

  const fetchImages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/profile/pictures`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        const allPics = (data.pictures || []).filter((p) => !p.is_default);
        setImages(allPics.filter((p) => p.image_type === 'gallery'));
      }
    } catch (e) { console.error(e); }
  }, [sessionToken]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    if (images.length + files.length > maxImages) {
      alert(t('profile.alerts.maxFacilityImages', { max: maxImages }));
      return;
    }
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('image_type', 'gallery');
      try {
        await axios.post(`${API}/api/profile/upload-picture`, fd, {
          headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'multipart/form-data' }
        });
      } catch (err) { console.error(err); }
    }
    await fetchImages();
    onUpdate();
    setUploading(false);
  };

  const handleRemove = async (picId) => {
    if (!window.confirm('Remove this image?')) return;
    try {
      await fetch(`${API}/api/profile/picture/${picId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      await fetchImages();
      onUpdate();
    } catch (e) { alert(t('profile.alerts.removeImageFailed')); }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t('profile.photos.facilityPictures')} <span className="text-gray-400 font-normal">— {images.length}/{maxImages}</span>
      </label>
      <p className="mb-2 text-xs text-amber-700" data-testid="facility-approval-note">
        {t('profile.photos.facilityReviewNote')}
      </p>
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
          {images.map((img) => (
            <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200" data-testid={`facility-image-${img.id}`}>
              <img
                src={img.is_default ? img.file_url : `${API}${img.file_url}${img.approval_status === 'approved' ? '' : `?auth=${sessionToken}`}`}
                alt="Facility"
                className="w-full h-full object-cover"
              />
              {img.approval_status && img.approval_status !== 'approved' && (
                <span
                  data-testid={`facility-image-status-${img.approval_status}`}
                  className={`absolute bottom-1 left-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase text-white ${
                    img.approval_status === 'pending' ? 'bg-amber-500' : 'bg-rose-600'
                  }`}
                >
                  {img.approval_status === 'pending' ? t('profile.photos.inReview') : t('profile.photos.rejected')}
                </span>
              )}
              <button
                onClick={() => handleRemove(img.id)}
                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
      {images.length < maxImages && (
        <PhotoPicker
          multiple
          maxFiles={maxImages - images.length}
          onConfirm={handleUpload}
          uploading={uploading}
          testId="facility-picture-picker"
          triggerClassName="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors"
        >
          {uploading ? (
            <span className="flex items-center text-sm text-gray-500">
              <span className="animate-spin mr-2 h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></span>
              {t('profile.photos.uploading')}
            </span>
          ) : (
            <span className="text-sm text-gray-500">
              <span className="text-blue-600 font-medium">{t('profile.photos.clickToUpload')}</span> {t('profile.photos.facilityUploadHint')}
            </span>
          )}
        </PhotoPicker>
      )}
    </div>
  );
};

/* ─── License Document Manager (doctors & providers) ─── */
const LicenseDocumentManager = ({ sessionToken, onUpdate }) => {
  const { t } = useTranslation();
  const [license, setLicense] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const fetchLicense = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/license/my`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLicense(data.license);
      }
    } catch (e) { console.error(e); }
  }, [sessionToken]);

  useEffect(() => { fetchLicense(); }, [fetchLicense]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = /^(image\/(jpeg|jpg|png))|application\/pdf$/i.test(file.type);
    if (!ok) { alert(t('profile.alerts.invalidLicenseFile')); return; }
    if (file.size > 8 * 1024 * 1024) { alert(t('profile.alerts.maxLicenseSize')); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/api/license/upload`, fd, {
        headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'multipart/form-data' },
      });
      await fetchLicense();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(t('profile.alerts.uploadFailed') + ': ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
    e.target.value = '';
  };

  // Authed preview fetch — converts into a blob URL
  const openPreview = async () => {
    if (!license) return;
    try {
      const res = await fetch(`${API}${license.url}`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (!res.ok) throw new Error('Failed to load license');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      window.open(url, '_blank');
    } catch (e) { alert(t('profile.alerts.openLicenseFailed')); }
  };

  const isPdf = license?.content_type === 'application/pdf';

  return (
    <div data-testid="license-upload-manager">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t('profile.license.title')} <span className="text-red-500">*</span>
        <span className="text-gray-400 font-normal ml-2">— {t('profile.license.requiredForVerification')}</span>
      </label>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50 flex items-center justify-center flex-shrink-0">
          {license ? (
            isPdf ? (
              <div className="text-center">
                <div className="text-rose-600 font-bold text-xs">PDF</div>
                <div className="text-gray-500 text-[10px] mt-1">{t('profile.license.title')}</div>
              </div>
            ) : (
              <button type="button" onClick={openPreview} className="w-full h-full">
                <div className="w-full h-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-xs font-semibold">
                  {t('profile.license.uploaded')}
                </div>
              </button>
            )
          ) : (
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors" data-testid="license-upload-btn">
            {uploading ? (
              <><span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> {t('profile.photos.uploading')}</>
            ) : (
              <>{license ? t('profile.license.replace') : t('profile.license.upload')}</>
            )}
            <input type="file" accept="image/jpeg,image/jpg,image/png,application/pdf" onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
          {license && (
            <button
              type="button"
              onClick={openPreview}
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 border border-gray-200 transition-colors"
              data-testid="license-view-btn"
            >
              {t('profile.license.view')}
            </button>
          )}
          <p className="text-xs text-gray-400">{t('profile.license.hint')}</p>
          {license && (
            <p className="text-xs text-emerald-600">
              ✓ {t('profile.license.uploaded')} · {license.extension?.toUpperCase()} · {t('profile.license.awaitingReview')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};



/* ─── Verification Banner ─── */
const VerificationBanner = ({ status, onApply, applying, checklist }) => {
  const { t } = useTranslation();
  if (status === 'verified') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-testid="verification-verified-banner">
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <p className="font-semibold text-green-800">{t('profile.verification.verified')}</p>
          <p className="text-sm text-green-600">{t('profile.verification.verifiedSub')}</p>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3" data-testid="verification-pending-banner">
        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <p className="font-semibold text-amber-800">{t('profile.verification.pending')}</p>
          <p className="text-sm text-amber-600">{t('profile.verification.pendingSub')}</p>
        </div>
      </div>
    );
  }

  // not_started
  const allComplete = checklist?.has_name && checklist?.has_description && checklist?.has_location && checklist?.has_picture;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="verification-not-started-banner">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <div>
          <p className="font-semibold text-blue-800">{t('profile.verification.notStarted')}</p>
          <p className="text-sm text-blue-600">{t('profile.verification.notStartedSub')}</p>
        </div>
      </div>
      
      {/* Checklist */}
      {checklist && (
        <div className="grid grid-cols-2 gap-2 mb-4 ml-13">
          {[
            { key: 'has_name', label: t('profile.verification.checkName') },
            { key: 'has_description', label: t('profile.verification.checkDescription') },
            { key: 'has_location', label: t('profile.verification.checkLocation') },
            { key: 'has_picture', label: t('profile.verification.checkPicture') },
          ].map(item => (
            <div key={item.key} className="flex items-center gap-2 text-sm">
              {checklist[item.key] ? (
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                </svg>
              )}
              <span className={checklist[item.key] ? 'text-green-700' : 'text-gray-500'}>{item.label}</span>
            </div>
          ))}
        </div>
      )}

      {allComplete && (
        <button
          onClick={onApply}
          disabled={applying}
          className="w-full mt-1 bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
          data-testid="apply-verification-btn"
        >
          {applying ? t('profile.verification.applying') : t('profile.verification.apply')}
        </button>
      )}
    </div>
  );
};


/* ─── Main ProfilePage ─── */
const ProfilePage = () => {
  const { user } = useUnifiedAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Provider-specific state
  const [providerId, setProviderId] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [applyingVerification, setApplyingVerification] = useState(false);
  const [providerData, setProviderData] = useState({
    name: '',
    description: '',
    area: '',
    category: '',
    google_maps_link: '',
    phone: '',
    email: '',
    tags: [],
    services_for: 'mixed'
  });  const [newTag, setNewTag] = useState('');
  const [editMode, setEditMode] = useState(false);
  const sessionToken = localStorage.getItem('sessionToken');

  const fetchSubscription = async () => {
    if (!sessionToken) return;
    try {
      const response = await axios.get(`${API}/api/provider/subscription`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchVerificationStatus = async () => {
    if (!sessionToken) return;
    try {
      const res = await axios.get(`${API}/api/provider/verification-status`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setVerificationData(res.data);
    } catch (e) {
      console.error('Error fetching verification status:', e);
    }
  };

  const fetchProviderProfile = async () => {
    try {
      const response = await axios.get(`${API}/api/my-provider-profile`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data && response.data.profile) {
        const provider = response.data.profile;
        setProviderId(provider.id);
        setProviderData({
          name: provider.name || '',
          description: provider.description || '',
          area: provider.area || '',
          category: provider.category || '',
          google_maps_link: provider.google_maps_link || '',
          phone: provider.phone || '',
          email: provider.email || '',
          tags: provider.tags || [],
          services_for: provider.services_for || 'mixed'
        });
      }
    } catch (error) {
      console.error('Error fetching provider profile:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchCompleteProfile();
      if (user.user_type === 'provider') {
        fetchProviderProfile();
        fetchSubscription();
        fetchVerificationStatus();
      }
    }
  }, [user, sessionToken]);

  const fetchCompleteProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/my-profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('sessionToken')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
      } else {
        setProfileData(user);
      }
    } catch (error) {
      console.error('Error fetching complete profile data:', error);
      setProfileData(user);
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = () => {
    fetchCompleteProfile();
    if (user?.user_type === 'provider') {
      fetchProviderProfile();
      fetchVerificationStatus();
    }
  };

  const getUserTypeLabel = (userType) => t(`profile.userTypes.${userType}`) || t('profile.userTypes.patient');
  // Providers share one profile screen - their category is the only label that differs.
  const getRoleLabel = (userType) => {
    const key = CATEGORY_LABEL_KEYS[providerData.category];
    if (userType === 'provider' && key) return t(key[0], key[1]);
    return getUserTypeLabel(userType);
  };
  const getUserTypeDescription = (userType) => t(`profile.descriptions.${userType}`) || t('profile.descriptions.patient');

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };
  
  const handleProviderSave = async () => {
    if (!providerId) { alert(t('profile.alerts.providerIdMissing')); return; }
    try {
      await axios.put(
        `${API}/api/healthcare-providers/${providerId}`,
        providerData,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      alert(t('profile.alerts.profileUpdated'));
      setEditMode(false);
      fetchProviderProfile();
      fetchVerificationStatus();
    } catch (error) {
      console.error('Error updating provider profile:', error);
      alert(t('profile.alerts.profileUpdateFailed') + ': ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleApplyVerification = async () => {
    setApplyingVerification(true);
    try {
      const res = await axios.post(`${API}/api/provider/request-verification`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      alert(res.data.message);
      fetchVerificationStatus();
    } catch (err) {
      alert(err.response?.data?.detail || t('profile.alerts.verificationRequestFailed'));
    }
    setApplyingVerification(false);
  };
  
  const addTag = () => {
    if (newTag.trim() && !providerData.tags.includes(newTag.trim())) {
      setProviderData({ ...providerData, tags: [...providerData.tags, newTag.trim()] });
      setNewTag('');
    }
  };
  
  const removeTag = (tagToRemove) => {
    setProviderData({ ...providerData, tags: providerData.tags.filter(tag => tag !== tagToRemove) });
  };

  const formatGender = (gender) => gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : 'Not specified';
  const formatPhone = (phone) => phone || 'Not provided';

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('common.error')}</h2>
          <p className="text-gray-600">{t('errors.unauthorized')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  const currentProfile = profileData || user;
  const isPatient = currentProfile.user_type === 'patient' || currentProfile.user_type === 'user';
  const isProvider = currentProfile.user_type === 'provider';
  const isDoctor = currentProfile.user_type === 'doctor';
  const displayAge = currentProfile.age || calculateAge(currentProfile.date_of_birth);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Profile Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-8">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-6">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-5xl font-bold text-white flex-shrink-0">
                {currentProfile.picture ? (
                  <img src={currentProfile.picture} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  currentProfile.name?.charAt(0) || currentProfile.email?.charAt(0) || 'U'
                )}
              </div>
              <div className="flex-1 text-center md:text-start">
                <h1 className="text-3xl font-bold mb-2">
                  {currentProfile.name || `${currentProfile.first_name || ''} ${currentProfile.last_name || ''}`.trim() || currentProfile.email}
                </h1>
                <p className="text-blue-100 mb-1" data-testid="profile-role-label">{getRoleLabel(currentProfile.user_type)}</p>
                <p className="text-sm text-blue-100 max-w-2xl">{getUserTypeDescription(currentProfile.user_type)}</p>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="p-6">
            {/* Verification Banner for Providers */}
            {isProvider && verificationData && (
              <div className="mb-6">
                <VerificationBanner
                  status={verificationData.verification_status}
                  onApply={handleApplyVerification}
                  applying={applyingVerification}
                  checklist={verificationData.checklist}
                />
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'profile' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('profile.tabs.overview') || 'Overview'}
              </button>
              {isPatient && (
                <button
                  onClick={() => setActiveTab('health')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'health' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('profile.tabs.healthData')}
                </button>
              )}
              {isDoctor && (
                <button
                  onClick={() => setActiveTab('doctor')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'doctor' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Professional Profile
                </button>
              )}
            </div>

            {/* Profile Overview Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {/* Contact Information */}
                <div className="border-b border-gray-200 pb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    {t('profile.sections.contactInfo') || 'Contact Information'}
                  </h2>
                  {(isProvider || isDoctor) ? (
                    <ContactInfoChange
                      user={currentProfile}
                      sessionToken={sessionToken}
                      onChanged={() => { /* request submitted */ }}
                    />
                  ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{t('profile.fields.email')}</label>
                      <p className="text-gray-900">{currentProfile.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">{t('profile.fields.phone')}</label>
                      <p className="text-gray-900">{formatPhone(currentProfile.phone)}</p>
                    </div>
                    {!isProvider && currentProfile.date_of_birth && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('profile.fields.age')}</label>
                        <p className="text-gray-900">{displayAge} years old</p>
                      </div>
                    )}
                    {!isProvider && currentProfile.gender && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('profile.fields.gender')}</label>
                        <p className="text-gray-900">{formatGender(currentProfile.gender)}</p>
                      </div>
                    )}
                  </div>
                  )}
                </div>

                {/* Subscription Tier - Only for verified providers */}
                {isProvider && subscriptionData && subscriptionData.current_tier && (
                  <div className="mt-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('profile.provider.subscriptionPlan')}</h2>
                    <div
                      onClick={() => navigate('/pricing')}
                      className="p-6 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-2xl font-bold text-white">{t('profile.provider.planWithTier', { tier: t(`profile.provider.tiers.${subscriptionData.current_tier.tier_name}`, subscriptionData.current_tier.tier_name) })}</h3>
                            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                              subscriptionData.current_tier.tier_name === 'Free'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-white text-blue-600'
                            }`}>
                              {subscriptionData.current_tier.tier_name === 'Free' ? t('profile.provider.active') : t('profile.provider.premium')}
                            </span>
                          </div>
                          <p className="text-blue-100 mb-4">
                            {subscriptionData.current_tier.tier_name === 'Free' ? t('profile.provider.freeForever') : `${subscriptionData.current_tier.price_monthly} ${t('profile.provider.perMonth')}`}
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                            <div className="bg-white bg-opacity-20 rounded-lg p-3">
                              <p className="text-xs text-blue-100">{t('profile.provider.staff')}</p>
                              <p className="text-lg font-bold text-white">{subscriptionData.usage.staff_count}/{subscriptionData.usage.staff_limit}</p>
                            </div>
                            <div className="bg-white bg-opacity-20 rounded-lg p-3">
                              <p className="text-xs text-blue-100">{t('profile.provider.branches')}</p>
                              <p className="text-lg font-bold text-white">{subscriptionData.usage.branch_count}/{subscriptionData.usage.branch_limit}</p>
                            </div>
                            <div className="bg-white bg-opacity-20 rounded-lg p-3">
                              <p className="text-xs text-blue-100">{t('profile.provider.commission')}</p>
                              <p className="text-lg font-bold text-white">{subscriptionData.current_tier.commission_rate ?? subscriptionData.current_tier.features?.commission_rate}%</p>
                            </div>
                          </div>
                        </div>
                        <div className="ml-4 flex flex-col items-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate('/pricing'); }}
                            className="bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors font-semibold text-sm"
                          >
                            {subscriptionData.current_tier.tier_name === 'Free' ? t('profile.provider.upgradePlan') : t('profile.provider.manage')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Insurance - non-providers */}
                {!isProvider && currentProfile.insurance_data && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg">
                    <h3 className="text-sm font-medium text-green-900 mb-2">Insurance Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div><span className="text-green-600 font-medium">Company:</span> {currentProfile.insurance_data.insurance_company}</div>
                      <div><span className="text-green-600 font-medium">Policy #:</span> {currentProfile.insurance_data.insurance_number}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Provider Settings */}
            {activeTab === 'profile' && isProvider && (
              <div className="space-y-6 mt-6">
                {!verificationData?.is_verified && <VerificationStatusBanner sessionToken={sessionToken} />}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">{t('profile.provider.providerSettings')}</h2>
                    <button
                      onClick={() => setEditMode(!editMode)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        editMode
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      data-testid="provider-edit-toggle"
                    >
                      {editMode ? t('profile.provider.cancel') : t('profile.provider.editProfile')}
                    </button>
                  </div>
                  
                  {/* Profile Picture & Facility Images */}
                  <div className="space-y-6 mb-6">
                    <ProfilePictureManager
                      sessionToken={sessionToken}
                      onUpdate={refreshAll}
                      currentPicture={currentProfile.picture}
                    />
                    <FacilityImagesManager
                      sessionToken={sessionToken}
                      onUpdate={refreshAll}
                    />
                    <LicenseDocumentManager
                      sessionToken={sessionToken}
                      onUpdate={refreshAll}
                    />
                  </div>
                  
                  {/* Provider Info Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.provider.facilityName')} *</label>
                      {editMode ? (
                        <input type="text" value={providerData.name}
                          onChange={(e) => setProviderData({...providerData, name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          data-testid="provider-name-input"
                        />
                      ) : (
                        <p className="text-gray-900" data-testid="provider-name-value">{providerData.name || <span className="text-red-400 italic">{t('profile.provider.required')}</span>}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.provider.location')} *</label>
                      {editMode ? (
                        <input type="text" value={providerData.area}
                          onChange={(e) => setProviderData({...providerData, area: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={t('profile.provider.locationPlaceholder')}
                          data-testid="provider-area-input"
                        />
                      ) : (
                        <p className="text-gray-900" data-testid="provider-area-value">{providerData.area || <span className="text-red-400 italic">{t('profile.provider.required')}</span>}</p>
                      )}
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.provider.googleMapsLink')}</label>
                      {editMode ? (
                        <input type="text" value={providerData.google_maps_link}
                          onChange={(e) => setProviderData({...providerData, google_maps_link: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://maps.google.com/..."
                        />
                      ) : (
                        <p className="text-gray-900">{providerData.google_maps_link || t('profile.provider.notSet')}</p>
                      )}
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.provider.description')} *</label>
                      {editMode ? (
                        <textarea value={providerData.description}
                          onChange={(e) => setProviderData({...providerData, description: e.target.value})}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder={t('profile.provider.descriptionPlaceholder')}
                        />
                      ) : (
                        <p className="text-gray-900">{providerData.description || <span className="text-red-400 italic">{t('profile.provider.required')}</span>}</p>
                      )}
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.provider.servicesFor')}</label>
                      {editMode ? (
                        <select value={providerData.services_for}
                          onChange={(e) => setProviderData({...providerData, services_for: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          data-testid="provider-services-for-select"
                        >
                          <option value="mixed">{t('profile.provider.servicesForMixed')}</option>
                          <option value="male">{t('profile.provider.servicesForMale')}</option>
                          <option value="female">{t('profile.provider.servicesForFemale')}</option>
                        </select>
                      ) : (
                        <p className="text-gray-900" data-testid="provider-services-for-value">
                          {providerData.services_for === 'male' && t('profile.provider.servicesForMale')}
                          {providerData.services_for === 'female' && t('profile.provider.servicesForFemale')}
                          {providerData.services_for === 'mixed' && t('profile.provider.servicesForMixed')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Tags */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.provider.tagsLabel')}</label>
                    {editMode ? (
                      <div>
                        <div className="flex gap-2 mb-2">
                          <input type="text" value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder={t('profile.provider.addTag')}
                          />
                          <button onClick={addTag} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{t('profile.provider.add')}</button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {providerData.tags.map((tag, idx) => (
                            <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                              {tag}
                              <button onClick={() => removeTag(tag)} className="text-blue-600 hover:text-blue-800">x</button>
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {providerData.tags.length > 0 ? providerData.tags.map((tag, idx) => (
                          <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">{tag}</span>
                        )) : <p className="text-gray-500">{t('profile.provider.noTags')}</p>}
                      </div>
                    )}
                  </div>
                  
                  {editMode && (
                    <button onClick={handleProviderSave}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                      data-testid="provider-save-btn"
                    >
                      {t('profile.provider.saveChanges')}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Health Tracking Tab */}
            {activeTab === 'health' && isPatient && <HealthTracking />}

            {/* Doctor Profile Builder Tab */}
            {activeTab === 'doctor' && isDoctor && (
              <div className="mt-6 space-y-6">
                <VerificationStatusBanner sessionToken={sessionToken} />
                <div className="border-t border-gray-200 pt-6 space-y-6">
                  <ProfilePictureManager
                    sessionToken={sessionToken}
                    onUpdate={refreshAll}
                    currentPicture={currentProfile.picture}
                  />
                  <LicenseDocumentManager
                    sessionToken={sessionToken}
                    onUpdate={refreshAll}
                  />
                </div>
                <DoctorProfileBuilder />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
